'use client';

import { CheckCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

interface PlanCardProps {
    translationKey: string;
    price: number;
    isSelected: boolean;
    onToggle: () => void;
}

function formatCurrency(value: number) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(value);
}

export function PlanCard({ translationKey, price, isSelected, onToggle }: PlanCardProps) {
    const tPricing = useTranslations('pricing');

    // Strip 'pricing.' prefix if present since we're already using 'pricing' namespace
    const cleanKey = translationKey.startsWith('pricing.') 
        ? translationKey.replace(/^pricing\./, '') 
        : translationKey;

    // Safely access nested translation keys
    const getTranslation = (key: string, fallback: string = '') => {
        try {
            const fullKey = `${cleanKey}.${key}`;
            return tPricing(fullKey) || fallback;
        } catch {
            return fallback;
        }
    };

    const name = getTranslation('name', translationKey);
    const description = getTranslation('description', '');
    const features = [
        getTranslation('features.0', ''),
        getTranslation('features.1', ''),
        getTranslation('features.2', ''),
        getTranslation('features.3', ''),
    ].filter(Boolean);

    return (
        <div
            className={cn(
                'rounded-lg border-2 p-4 transition-all',
                isSelected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300'
            )}
        >
            <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{name}</h4>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-1">
                        {formatCurrency(price)}
                    </p>
                </div>
            </div>
            <ul className="space-y-1.5 mt-3 mb-4">
                {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>
            <button
                onClick={onToggle}
                className={cn(
                    'w-full px-4 py-2 rounded-lg font-medium transition-colors',
                    isSelected
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-primary-600 hover:bg-primary-700 text-white'
                )}
            >
                {isSelected ? tPricing('plans.remove') : tPricing('plans.choose')}
            </button>
        </div>
    );
}

